import React, { useEffect } from "react";
import { Save } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAddNewDoctorMutation } from "../../../State/Api/PharmacySaleApiSlice";
import { X } from "lucide-react";

const DISTRICT_STATE_MAP = {
  Visakhapatnam: "Andhra Pradesh",
  Vijayawada: "Andhra Pradesh",
  Hyderabad: "Telangana",
};

const validationSchema = yup.object({
  stateName: yup.string().trim().required("State is required"),
  doctorFullName: yup
    .string()
    .trim()
    .required("Doctor's name is required")
    .min(2, "Enter a valid name"),
  registrationNo: yup.string().trim().nullable(),
  specialization: yup.string().trim().nullable(),
  hospitalName: yup.string().trim().nullable(),
  doctorAddress: yup.string().trim().nullable(),
  mobileNo: yup
    .string()
    .nullable()
    .test(
      "is-valid-mobile",
      "Mobile must be 10 digits",
      (val) => !val || /^\d{10}$/.test(val)
    ),
  commissionPercent: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" ? null : v))
    .min(0, "Must be >= 0")
    .max(100, "Must be <= 100"),
  shareCommissionType: yup
    .string()
    .oneOf(["sales", "profit"], "Invalid commission type")
    .required("Commission type required"),
});

export default function DoctorFormModal({
  open,
  onClose,
  onSave,
  editingDoctor = null,
  districts = [],
}) {
  const [addNewDoctor] = useAddNewDoctorMutation();

  const formik = useFormik({
    initialValues: {
      doctorID: 0,
      pharmaID: localStorage.getItem("userUniqueID"),
      stateName: editingDoctor?.stateName || "",
      doctorFullName: editingDoctor?.doctorFullName || "",
      registrationNo: editingDoctor?.registrationNo || "",
      specialization: editingDoctor?.specialization || "",
      hospitalName: editingDoctor?.hospitalName || "",
      doctorAddress: editingDoctor?.doctorAddress || "",
      mobileNo: editingDoctor?.mobileNo || "",
      commissionPercent:
        editingDoctor?.commissionPercent != null
          ? String(editingDoctor.commissionPercent)
          : "",
      shareCommissionType:
        editingDoctor?.shareCommissionType || "sales", // default sales
      // keep district if you use it elsewhere in your app
      district: editingDoctor?.district || districts[0] || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      // Normalize payload to API fields and types
      const payload = {
        doctorID: 0,
        pharmaID: localStorage.getItem("userUniqueID"),
        stateName: values.stateName?.trim() || null,
        doctorFullName: values.doctorFullName?.trim().startsWith("Dr.")
          ? values.doctorFullName.trim()
          : `Dr. ${values.doctorFullName.trim()}`,
        registrationNo: values.registrationNo?.trim() || null,
        specialization: values.specialization?.trim() || null,
        hospitalName: values.hospitalName?.trim() || null,
        doctorAddress: values.doctorAddress?.trim() || null,
        mobileNo: values.mobileNo ? values.mobileNo.replace(/[^\d]/g, "") : null,
        commissionPercent:
          values.commissionPercent === "" || values.commissionPercent == null
            ? null
            : Number(values.commissionPercent),
        shareCommissionType: values.shareCommissionType,
      };
      try{
        const result = await addNewDoctor(payload).unwrap();
        onSave?.({
          name: payload.doctorFullName,
        });
        formik.resetForm();
      }catch(error){
        console.error(error);
      }

      
    },
  });

  useEffect(() => {
    if (open && formik.values.district) {
      // if district exists map to state
      const s = DISTRICT_STATE_MAP[formik.values.district];
      if (s && s !== formik.values.stateName) {
        formik.setFieldValue("stateName", s);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formik.values.district]);

  if (!open) return null;

  // helper to count mobile digits
  const mobileLen = (formik.values.mobileNo || "").replace(/\D/g, "").length;

    const handleSubmit = (e) => {
    formik.submitForm();
  };

  return (
    <div
      className="modal fade show d-block app-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        style={{ zIndex: "1060" }}
        role="document"
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-content shadow-lg app-modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title">
                {editingDoctor ? "Edit Doctor" : "Add New Doctor"}
              </h5>

              

              <button
              onClick={onClose}
              style={{"background":"rgb(254, 226, 226)","border":"none","borderRadius":"6px","width":"36px","height":"36px","cursor":"pointer","display":"flex","alignItems":"center","justifyContent":"center"}}
              className="ml-auto"
            >
              <X size={18} color="#dc2626" />
            </button>
            </div>

            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    STATE NAME <span className="text-danger">*</span>
                  </label>
                  <input
                    name="stateName"
                    className={`form-control app-control ${
                      formik.touched.stateName && formik.errors.stateName
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.stateName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="State"
                  />
                  {formik.touched.stateName && formik.errors.stateName && (
                    <div className="invalid-feedback">
                      {formik.errors.stateName}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    DOCTOR'S FULL NAME <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    
                    <input
                      name="doctorFullName"
                      className={`form-control app-control ${
                        formik.touched.doctorFullName &&
                        formik.errors.doctorFullName
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Enter Doctor's Name"
                      value={formik.values.doctorFullName.replace(
                        /^Dr\.\s*/i,
                        ""
                      )}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "doctorFullName",
                          e.target.value.replace(/^Dr\.\s*/i, "")
                        )
                      }
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.doctorFullName &&
                    formik.errors.doctorFullName && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.doctorFullName}
                      </div>
                    )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    REGISTRATION NO <span className="text-muted">Optional</span>
                  </label>
                  <input
                    name="registrationNo"
                    className="form-control app-control"
                    placeholder="Enter Registration no"
                    value={formik.values.registrationNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.registrationNo && formik.errors.registrationNo && (
                    <div className="invalid-feedback d-block">
                      {formik.errors.registrationNo}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    SPECIALIZATION <span className="text-muted">Optional</span>
                  </label>
                  <input
                    name="specialization"
                    className="form-control app-control"
                    placeholder="Type or Press Space Bar"
                    value={formik.values.specialization}
                    onKeyDown={(e) => {
                      if (e.key === " ") e.preventDefault();
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    HOSPITAL'S NAME <span className="text-muted">Optional</span>
                  </label>
                  <input
                    name="hospitalName"
                    className="form-control app-control"
                    placeholder="Enter Hospital's Name"
                    value={formik.values.hospitalName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    DOCTOR'S ADDRESS <span className="text-muted">Optional</span>
                  </label>
                  <input
                    name="doctorAddress"
                    className="form-control app-control"
                    placeholder="Enter Full Address Here..."
                    value={formik.values.doctorAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    MOBILE NO <span className="text-muted">Optional</span>
                  </label>
                  <div className="position-relative">
                    <input
                      name="mobileNo"
                      className={`form-control app-control ${
                        formik.touched.mobileNo && formik.errors.mobileNo
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Enter mobile no.."
                      value={formik.values.mobileNo || ""}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "mobileNo",
                          e.target.value.replace(/[^\d]/g, "").slice(0, 10)
                        )
                      }
                      onBlur={formik.handleBlur}
                    />
                    <small className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted">
                      {mobileLen}/10
                    </small>
                    {formik.touched.mobileNo && formik.errors.mobileNo && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.mobileNo}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    COMMISSION % <span className="text-muted">Optional</span>
                  </label>
                  <input
                    name="commissionPercent"
                    className={`form-control app-control ${
                      formik.touched.commissionPercent &&
                      formik.errors.commissionPercent
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="Enter Commission %"
                    value={formik.values.commissionPercent}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "commissionPercent",
                        e.target.value.replace(/[^\d.]/g, "")
                      )
                    }
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.commissionPercent &&
                    formik.errors.commissionPercent && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.commissionPercent}
                      </div>
                    )}
                </div>

                <div className="col-12">
                  <label className="form-label d-block">SHARE COMMISSION ON</label>
                  <div className="d-flex gap-4">
                    <label className="form-check-label">
                      <input
                        type="radio"
                        name="shareCommissionType"
                        className="form-check-input me-2"
                        value="sales"
                        checked={formik.values.shareCommissionType === "sales"}
                        onChange={() =>
                          formik.setFieldValue("shareCommissionType", "sales")
                        }
                      />
                      SALES AMT
                    </label>
                    <label className="form-check-label">
                      <input
                        type="radio"
                        name="shareCommissionType"
                        className="form-check-input me-2"
                        value="profit"
                        checked={formik.values.shareCommissionType === "profit"}
                        onChange={() =>
                          formik.setFieldValue("shareCommissionType", "profit")
                        }
                      />
                      PROFIT AMT
                    </label>
                  </div>
                  {formik.touched.shareCommissionType &&
                    formik.errors.shareCommissionType && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.shareCommissionType}
                      </div>
                    )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                className="gradient-button ml-auto"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
          
        </form>
      </div>
      <div className="modal-backdrop show"></div>
    </div>
  );
}
